/**
 * SignContractPage - Page publique de signature electronique du mandat.
 * Accessible via /sign/<token> sans authentification.
 *
 * Flow :
 *  1. Client ouvre le lien recu par email
 *  2. Lit le contrat (HTML integral rendu dans un iframe sandboxe)
 *  3. Dessine sa signature sur le canvas (souris ou doigt mobile)
 *  4. Saisit son nom complet + coche "Je lis et j'accepte les termes"
 *  5. Clique sur "Signer" -> POST /api/contracts/public/<token>/sign
 *  6. Confirmation + bouton telecharger le PDF signe
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SignContractPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [fullName, setFullName] = useState('');
  const [accept, setAccept] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const canvasRef = useRef(null);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    axios.get(`${API}/contracts/public/${token}`)
      .then(r => {
        setData(r.data);
        const f = r.data?.lead?.first_name || '';
        const l = r.data?.lead?.last_name || '';
        setFullName(`${f} ${l}`.trim());
        setAddress(r.data?.lead?.address || '');
        setPostalCode(r.data?.lead?.postal_code || '');
        setCity(r.data?.lead?.city || '');
      })
      .catch(e => {
        if (!e.response) {
          setError('Connexion au serveur impossible. Verifiez votre connexion internet ou contactez RealEase.');
        } else if (e.response.status === 410) {
          setError('Ce mandat a ete annule. Merci de contacter RealEase pour recevoir un nouveau lien.');
        } else if (e.response.status === 404) {
          setError('Mandat introuvable. Le lien est peut-etre incorrect ou ce mandat a ete supprime.');
        } else {
          setError(e.response?.data?.detail || 'Erreur inattendue.');
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  // Canvas drawing
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    let drawing = false;
    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
      return { x: x * scaleX, y: y * scaleY };
    };
    const start = (e) => { e.preventDefault(); drawing = true; const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawing) return; e.preventDefault(); const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasDrawn(true); };
    const end = () => { drawing = false; };

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', end);
    canvas.addEventListener('mouseleave', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);
    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      canvas.removeEventListener('mouseup', end);
      canvas.removeEventListener('mouseleave', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, [data, success]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const handleSign = async () => {
    if (!hasDrawn) { alert('Merci de dessiner votre signature.'); return; }
    if (!fullName.trim() || fullName.trim().length < 3) { alert('Merci de saisir votre nom complet.'); return; }
    if (!address.trim() || address.trim().length < 5) { alert('Merci de renseigner votre adresse postale (numero et rue).'); return; }
    if (!postalCode.trim() || postalCode.trim().length < 4) { alert('Merci de renseigner votre code postal.'); return; }
    if (!city.trim() || city.trim().length < 2) { alert('Merci de renseigner votre ville.'); return; }
    if (!accept) { alert('Vous devez accepter les termes du mandat.'); return; }

    const canvas = canvasRef.current;
    const signaturePng = canvas.toDataURL('image/png');

    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/contracts/public/${token}/sign`, {
        signature_png_base64: signaturePng,
        full_name: fullName.trim(),
        address: address.trim(),
        postal_code: postalCode.trim(),
        city: city.trim(),
        accept_terms: true,
      });
      setSuccess(r.data);
    } catch (e) {
      alert('Erreur : ' + (e.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="text-gray-500">Chargement du mandat...</div></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
        <svg className="w-16 h-16 mx-auto mb-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
        <h1 className="text-xl font-bold text-gray-900 mb-2">Lien invalide</h1>
        <p className="text-gray-600">{error}</p>
      </div>
    </div>
  );

  if (success) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6" data-testid="sign-success">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-xl p-10 text-center">
        <svg className="w-20 h-20 mx-auto mb-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Mandat signe avec succes !</h1>
        <p className="text-gray-600 mb-6">Votre Mandat de Recherche <b>{data.contract.number}</b> a ete signe electroniquement. Un email de confirmation vous a ete envoye avec le PDF signe.</p>
        <a href={`${API}/contracts/public/${token}/pdf`} target="_blank" rel="noreferrer" className="inline-block bg-[#008A8D] text-white font-semibold py-3 px-8 rounded-lg hover:bg-[#006B6D]" data-testid="download-signed-pdf">
          Telecharger le PDF signe
        </a>
        <p className="text-xs text-gray-400 mt-6">L'equipe RealEase commence des a present la recherche de votre bien.</p>
      </div>
    </div>
  );

  const c = data.contract;
  const l = data.lead;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="sign-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-[#008A8D] text-white p-6 rounded-t-xl">
          <h1 className="text-2xl font-bold">Mandat de Recherche {c.number}</h1>
          <p className="text-white/80 text-sm mt-1">Pour {l.first_name} {l.last_name} | RealEase</p>
        </div>

        {/* Contract body (sandboxed iframe) */}
        <div className="bg-white border-x border-gray-200 p-0">
          <iframe
            srcDoc={data.html}
            className="w-full"
            style={{ height: '700px', border: 'none' }}
            title="Mandat de Recherche"
            sandbox=""
            data-testid="contract-preview"
          />
        </div>

        {/* Signature area */}
        <div className="bg-white border border-gray-200 rounded-b-xl p-6 space-y-5">
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Signature electronique</h2>
            <p className="text-sm text-gray-500">Dessinez votre signature dans la zone ci-dessous (souris ou doigt sur mobile).</p>
          </div>

          <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50 relative">
            <canvas
              ref={canvasRef}
              width={800}
              height={200}
              className="w-full bg-white cursor-crosshair touch-none"
              style={{ maxHeight: '200px' }}
              data-testid="signature-canvas"
            />
            {!hasDrawn && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-gray-300 text-sm italic">Dessinez votre signature ici</span>
              </div>
            )}
          </div>
          <button onClick={clearCanvas} className="text-sm text-gray-500 hover:text-[#008A8D]" data-testid="clear-signature-btn">
            Effacer et recommencer
          </button>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Votre nom complet *</label>
            <input
              type="text"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008A8D] focus:border-[#008A8D]"
              placeholder="Prenom NOM"
              data-testid="sign-full-name"
              required
            />
          </div>

          {/* Adresse postale obligatoire pour le mandat */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3" data-testid="sign-address-block">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              <div>
                <h3 className="text-sm font-bold text-amber-900">Votre adresse postale (obligatoire)</h3>
                <p className="text-xs text-amber-800 mt-0.5">Mention legale du mandat. Doit correspondre a votre justificatif de domicile.</p>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Adresse (numero + rue) *</label>
              <input
                type="text"
                value={address}
                onChange={e => setAddress(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008A8D] focus:border-[#008A8D] bg-white"
                placeholder="12 rue de la Liberte"
                data-testid="sign-address-input"
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-1">
                <label className="block text-xs font-semibold text-gray-700 mb-1">Code postal *</label>
                <input
                  type="text"
                  value={postalCode}
                  onChange={e => setPostalCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008A8D] focus:border-[#008A8D] bg-white"
                  placeholder="29000"
                  inputMode="numeric"
                  data-testid="sign-postal-code-input"
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 mb-1">Ville *</label>
                <input
                  type="text"
                  value={city}
                  onChange={e => setCity(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008A8D] focus:border-[#008A8D] bg-white"
                  placeholder="Quimper"
                  data-testid="sign-city-input"
                  required
                />
              </div>
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={accept}
              onChange={e => setAccept(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#008A8D]"
              data-testid="sign-accept-terms"
            />
            <span className="text-sm text-gray-700 leading-relaxed">
              Je reconnais avoir lu et compris les termes du mandat ci-dessus, et je m'engage a le respecter.
              J'accepte que ma signature electronique ait la meme valeur juridique qu'une signature manuscrite.
            </span>
          </label>

          <button
            onClick={handleSign}
            disabled={submitting || !hasDrawn || !accept || !fullName.trim() || !address.trim() || address.trim().length < 5 || !postalCode.trim() || postalCode.trim().length < 4 || !city.trim()}
            className="w-full bg-[#008A8D] text-white font-semibold py-4 rounded-lg hover:bg-[#006B6D] disabled:opacity-50 disabled:cursor-not-allowed transition-all text-lg"
            data-testid="submit-signature-btn"
          >
            {submitting ? 'Signature en cours...' : 'Signer le mandat'}
          </button>

          <p className="text-xs text-gray-400 text-center">
            Votre signature, votre IP et la date seront enregistrees comme preuve. RealEase | <a href="mailto:contact@real-ease.fr" className="underline">contact@real-ease.fr</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignContractPage;
